import { isPlatformBrowser } from '@angular/common';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PaymentService } from './payment';
import { ToastService } from './toast';

/**
 * Fetches receipt bytes through HttpClient (so the auth interceptor attaches the JWT) and
 * hands them to the browser via a blob object-URL. This is the ONLY safe way to print/download
 * the authenticated receipt endpoints — a raw <a href>/window.open/<iframe> would 401 because
 * the browser won't send the Authorization header on a plain navigation.
 *
 * Every method is SSR-safe (no-ops on the server) and surfaces failures via the ToastService.
 */
@Injectable({ providedIn: 'root' })
export class ReceiptDownloader {
  private platformId = inject(PLATFORM_ID);
  private payment = inject(PaymentService);
  private toast = inject(ToastService);

  /** Download the PDF receipt. Falls back to HTML print if the PDF engine isn't available. */
  async downloadPdf(paymentId: number, receiptNo?: string | number): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      const blob = await firstValueFrom(this.payment.rcPdf(paymentId));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Receipt-${(receiptNo ?? paymentId).toString().replace(/[^\w-]/g, '')}.pdf`;
      document.body.appendChild(a);
      a.click();
      // Defer cleanup off the click tick: revoking the object-URL synchronously can abort the
      // download in Firefox/Safari before the browser has finished reading the blob.
      setTimeout(() => {
        a.remove();
        URL.revokeObjectURL(url);
      }, 0);
    } catch (err: any) {
      const msg = await this.readBlobError(err); // PDF errors come back as a JSON blob
      this.toast.error(msg || 'Could not generate the PDF receipt.');
      // Defensive fallback ONLY for an unexpected 500 (PDF engine error but the HTML is still
      // serviceable). On the 400 SUCCESS-gate the HTML endpoint 400s identically, so falling back
      // there would just double-toast and open a pointless print window.
      if (err?.status === 500) await this.printHtml(paymentId);
    }
  }

  /** Open the printable HTML receipt in a new window and trigger the print dialog. */
  async printHtml(paymentId: number): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    let html: string;
    try {
      html = await firstValueFrom(this.payment.rcHtml(paymentId));
    } catch (err) {
      const msg = await this.readBlobError(err);
      this.toast.error(msg || 'Receipt is available only for successful payments.');
      return;
    }
    const w = window.open('', '_blank', 'width=820,height=900');
    if (!w) {
      this.toast.error('Allow pop-ups to print the receipt.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    // Print once the receipt has rendered.
    w.onload = () => {
      w.focus();
      w.print();
    };
  }

  /**
   * The envelope message for a failed receipt request. The body arrives as a Blob for
   * responseType:'blob' (Download) and as a raw string for responseType:'text' (Html) —
   * both carry the JSON envelope, so parse either for `.message`.
   */
  private async readBlobError(err: any): Promise<string> {
    try {
      const body = err?.error;
      // Blob (Download / responseType:'blob') — read the text then parse.
      if (body instanceof Blob) {
        const text = await body.text();
        try {
          return JSON.parse(text)?.message ?? text;
        } catch {
          return text;
        }
      }
      // String (Html / responseType:'text') — the JSON envelope is delivered verbatim.
      if (typeof body === 'string') {
        try {
          return JSON.parse(body)?.message ?? body;
        } catch {
          return body;
        }
      }
      return body?.message ?? err?.message ?? '';
    } catch {
      return '';
    }
  }
}
